/* Birth-flower cart transport. Files and names are supplied by the renderer.
 * The recovery journal contains only identifiers, hashes and write receipts.
 * An unknown write is never replayed merely because a cart read is empty.
 * create({root,productId}) -> addBatch(items,{source,onProgress}), recover(),
 * restore(lineKey), inspect(), clearCompleted(), abandonChecked().
 * Items: {form:FormData,variantId:string,quantity:number,token:UUID}.
 * Freeze reviewed forms/tokens across retries. recover() never uploads forms.
 * Errors expose code, ambiguous, cartUrl and result (a partial public receipt).
 * abandonChecked() requires an explicit shopper action and keeps cart lines.
 */
(function (root) {
  'use strict';
  if (root.JEBirthflowerCart) return;
  const ID = /^[1-9]\d{0,23}$/, KEY = /^[1-9]\d{0,23}:[a-f0-9]{32}$/i;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const HASH = /^[a-f0-9]{64}$/, MAX_CART = 2 * 1024 * 1024;
  const running = new Set();
  const failure = (code, ambiguous = false) => Object.assign(new Error(code), { code, ambiguous });
  const count = value => Number.isInteger(value) && value >= 1 && value <= 99;
  const numericId = value => {
    if (typeof value === 'number' && !Number.isSafeInteger(value) || !ID.test(String(value))) throw failure('cart_invalid');
    return String(value);
  };
  const copy = value => JSON.parse(JSON.stringify(value));
  const planId = line => line.selling_plan_allocation?.selling_plan?.id == null ? null : numericId(line.selling_plan_allocation.selling_plan.id);
  const progress = (callback, value) => { try { callback?.(value); } catch {} };
  async function hash(bytes) {
    const result = await crypto.subtle.digest('SHA-256', typeof bytes === 'string' ? new TextEncoder().encode(bytes) : bytes);
    return Array.from(new Uint8Array(result), value => value.toString(16).padStart(2, '0')).join('');
  }
  async function propertiesHash(properties) {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) throw failure('cart_invalid');
    const pairs = Object.entries(properties).sort(([a], [b]) => a.localeCompare(b));
    if (pairs.length > 150 || pairs.some(([key, value]) => key.length > 200 || value !== null && typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean')) throw failure('cart_invalid');
    return hash(JSON.stringify(pairs));
  }
  async function readJson(response, signal) {
    const declared = response.headers.get('content-length');
    if (declared !== null && (!/^\d+$/.test(declared) || Number(declared) > MAX_CART)) throw failure('cart_invalid');
    const reader = response.body?.getReader();
    if (!reader) throw failure('cart_invalid');
    const chunks = []; let size = 0;
    const cancel = () => { reader.cancel().catch(() => {}); };
    signal.addEventListener('abort', cancel, { once: true });
    try {
      for (;;) {
        if (signal.aborted) throw failure('cart_unconfirmed', true);
        const { done, value } = await reader.read();
        if (done) break;
        size += value.byteLength;
        if (size > MAX_CART) { await reader.cancel(); throw failure('cart_invalid'); }
        chunks.push(value);
      }
      if (signal.aborted) throw failure('cart_unconfirmed', true);
    } finally { signal.removeEventListener('abort', cancel); reader.releaseLock(); }
    const bytes = new Uint8Array(size); let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  }
  function cleanSource(source, productId) {
    if (!source || !KEY.test(source.key || '') || !count(source.quantity) || !HASH.test(source.propertiesHash || '') ||
        numericId(source.productId) !== productId || !source.key.startsWith(numericId(source.variantId) + ':') ||
        source.token !== null && !UUID.test(source.token || '')) throw failure('cart_invalid');
    return { key: source.key, variantId: String(source.variantId), productId, quantity: source.quantity,
      propertiesHash: source.propertiesHash, sellingPlanId: source.sellingPlanId == null ? null : numericId(source.sellingPlanId), token: source.token };
  }
  function publicState(journal) {
    if (!journal) return null;
    return { id: journal.id, status: journal.phase, complete: journal.phase === 'complete',
      pending: journal.phase !== 'complete', uncertain: journal.phase === 'removing' || journal.rows.some(row => row.status === 'pending'),
      rows: journal.rows.map(({ token, variantId, quantity, status, key }) => ({ token, variantId, quantity, status, key: key || null })),
      source: journal.source ? { key: journal.source.key, variantId: journal.source.variantId, quantity: journal.source.quantity } : null };
  }
  function create(config = {}) {
    const kind = config.kind ?? 'birthflower';
    const prefixes = { birthflower: '_Birth flower ', sports: '_Sports ', wedding: '_Wedding ', nurse: '_Nurse ', pet: '_Pet ', groomsman: '_Groomsman ', 'script-name': '_Script name ', family: '_Family ', gift: '_Gift ', 'live-personalize': '_Live set ' };
    if (!Object.hasOwn(prefixes, kind)) throw failure('cart_invalid');
    const prefix = prefixes[kind];
    const foreignPrefixes = [...new Set([...Object.values(prefixes), '_Pet '])].filter(value => value !== prefix);
    const TOKEN = prefix + 'submission', PAYLOAD = prefix + 'payload';
    const productId = numericId(config.productId);
    let base;
    try { base = new URL(config.root || '/', location.origin); } catch { throw failure('cart_invalid'); }
    if (base.origin !== location.origin || base.username || base.password || base.search || base.hash ||
        !/^\/(?:[A-Za-z]{2,3}(?:-[A-Za-z]{2})?\/)?$/.test(base.pathname)) throw failure('cart_invalid');
    const cartUrl = new URL('cart', base).href;
    const urls = { read: cartUrl + '.js', add: cartUrl + '/add.js', change: cartUrl + '/change.js' };
    const storageKey = 'je-' + kind + ':cart:v1:' + base.pathname + ':' + productId;
    let currency = null;

    function read() {
      let raw;
      try { raw = sessionStorage.getItem(storageKey); } catch { throw failure('cart_storage_unavailable'); }
      if (!raw) return null;
      try {
        if (raw.length > 32768) throw failure('cart_invalid');
        const value = JSON.parse(raw);
        if (value.version !== 1 || value.productId !== productId || value.root !== base.pathname || !UUID.test(value.id || '') ||
            !['adding', 'removing', 'complete'].includes(value.phase) || !Array.isArray(value.rows) || !value.rows.length || value.rows.length > 24) throw failure('cart_invalid');
        const tokens = new Set();
        for (const row of value.rows) {
          numericId(row.variantId);
          if (!UUID.test(row.token || '') || tokens.has(row.token) || !count(row.quantity) || !HASH.test(row.payloadHash || '') ||
              !['ready', 'pending', 'confirmed', 'rejected'].includes(row.status) || typeof row.rejected !== 'boolean' ||
              row.status === 'confirmed' && (!KEY.test(row.key || '') || !row.key.startsWith(row.variantId + ':') || !HASH.test(row.cartHash || ''))) throw failure('cart_invalid');
          tokens.add(row.token);
        }
        if (value.source) value.source = cleanSource(value.source, productId);
        if (value.source && value.rows.length !== 1 || value.phase === 'removing' && !value.source ||
            value.phase !== 'adding' && value.rows.some(row => row.status !== 'confirmed')) throw failure('cart_invalid');
        return value;
      } catch { throw failure('cart_unconfirmed', true); }
    }
    function persist(journal) {
      const raw = JSON.stringify(journal);
      try {
        sessionStorage.setItem(storageKey, raw);
        if (sessionStorage.getItem(storageKey) !== raw) throw failure('cart_storage_unavailable');
      } catch { throw failure('cart_storage_unavailable', journal.rows.some(row => row.status === 'pending' || row.status === 'confirmed') || journal.phase === 'removing'); }
    }
    async function request(url, options = {}) {
      const controller = new AbortController(); let timer;
      const expired = new Promise((_, reject) => { timer = setTimeout(() => { controller.abort(); reject(failure('cart_unconfirmed', true)); }, 45000); });
      try {
        return await Promise.race([expired, (async () => {
          const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', redirect: 'error', ...options,
            headers: { Accept: 'application/json', ...options.headers }, signal: controller.signal });
          if (response.redirected) throw failure('cart_invalid');
          return { ok: response.ok, status: response.status, data: await readJson(response, controller.signal) };
        })()]);
      } finally { clearTimeout(timer); controller.abort(); }
    }
    async function cart() {
      const response = await request(urls.read);
      if (!response.ok || !Array.isArray(response.data?.items) || response.data.items.length > 1000) throw failure('cart_invalid');
      currency = response.data.currency || null;
      return response.data.items;
    }
    async function exclusive(operation) {
      if (running.has(storageKey)) throw failure('cart_busy', true);
      running.add(storageKey);
      try {
        const perform = async () => {
          try { return await operation(); }
          catch (error) {
            if (!error.code) error = failure('cart_unconfirmed', true);
            error.cartUrl = cartUrl;
            try { error.result = publicState(read()); } catch {}
            throw error;
          }
        };
        if (globalThis.navigator?.locks?.request) return await navigator.locks.request(storageKey, { ifAvailable: true }, lock => {
          if (!lock) throw failure('cart_busy', true);
          return perform();
        });
        return await perform();
      } finally { running.delete(storageKey); }
    }
    async function sourceFromLine(line, lines) {
      if (!line || !KEY.test(line.key || '') || numericId(line.product_id) !== productId || !count(line.quantity) ||
          !line.key.startsWith(numericId(line.variant_id ?? line.id) + ':') || planId(line) !== null || line.parent_relationship ||
          lines.some(item => item.parent_relationship?.parent_key === line.key) ||
          line.properties?.['_Studio submission'] || line.properties?.['_Studio upgrade for'] || line.properties?.['_Design file'] || line.properties?.['Design file'] ||
          foreignPrefixes.some(value => Object.keys(line.properties || {}).some(key => key.startsWith(value)))) throw failure('cart_restore_unavailable');
      const token = line.properties?.[TOKEN] || null;
      if (token !== null && !UUID.test(token)) throw failure('cart_restore_unavailable');
      return { key: line.key, variantId: numericId(line.variant_id ?? line.id), productId, quantity: line.quantity,
        propertiesHash: await propertiesHash(line.properties || {}), sellingPlanId: null, token };
    }
    async function matchesSource(line, source, lines) {
      if (!line) return false;
      try { return JSON.stringify(await sourceFromLine(line, lines)) === JSON.stringify(source); } catch { return false; }
    }
    async function relatedSource(lines, source, replacementKey) {
      for (const line of lines) {
        if (line.key === replacementKey || String(line.product_id) !== productId || String(line.variant_id ?? line.id) !== source.variantId || planId(line) !== source.sellingPlanId) continue;
        if (source.token && line.properties?.[TOKEN] === source.token || await propertiesHash(line.properties || {}) === source.propertiesHash) return true;
      }
      return false;
    }
    async function prepare(items) {
      if (!Array.isArray(items) || !items.length || items.length > 24) throw failure('cart_invalid');
      const tokens = new Set(), prepared = [];
      // Copy every caller-owned form before the first await.
      const frozen = items.map(item => {
        if (!(item?.form instanceof FormData) || !count(item.quantity) || !UUID.test(item.token || '') || tokens.has(item.token)) throw failure('cart_invalid');
        const variantId = numericId(item.variantId), form = new FormData(), fields = new Set();
        if (item.form.get('id') !== variantId || item.form.get('quantity') !== String(item.quantity)) throw failure('cart_invalid');
        if (kind === 'live-personalize' && item.form.get('properties[_Studio submission]') !== item.token) throw failure('cart_invalid');
        for (const [key, value] of item.form) {
          if (fields.has(key) || !['id', 'quantity'].includes(key) && !/^properties\[[^\]\r\n]{1,200}\]$/.test(key)) throw failure('cart_invalid');
          fields.add(key);
          if (key === `properties[${TOKEN}]` || key === `properties[${PAYLOAD}]`) continue;
          if (/^properties\[_?Studio (submission|upgrade for)\]$/.test(key) &&
              !(kind === 'live-personalize' && key === 'properties[_Studio submission]' && value === item.token)) throw failure('cart_invalid');
          if (foreignPrefixes.some(value => key.startsWith('properties[' + value))) throw failure('cart_invalid');
          form.append(key, value);
        }
        if (fields.size > 150) throw failure('cart_invalid');
        tokens.add(item.token);
        return { form, variantId, quantity: item.quantity, token: item.token };
      });
      for (const item of frozen) {
        const parts = []; let bytes = 0;
        for (const [key, value] of item.form) {
          if (typeof value === 'string') { bytes += value.length * 2; parts.push([key, 'text', value]); }
          else {
            bytes += value.size;
            if (bytes > 40 * 1024 * 1024) throw failure('cart_invalid');
            parts.push([key, 'file', value.name, value.type, value.size, await hash(await value.arrayBuffer())]);
          }
        }
        if (bytes > 40 * 1024 * 1024) throw failure('cart_invalid');
        parts.sort(([a], [b]) => a.localeCompare(b));
        const payloadHash = await hash(JSON.stringify(parts));
        item.form.set(`properties[${TOKEN}]`, item.token); item.form.set(`properties[${PAYLOAD}]`, payloadHash);
        prepared.push({ ...item, payloadHash });
      }
      return prepared;
    }
    async function confirmRows(journal, lines) {
      let changed = false;
      for (const row of journal.rows) {
        const found = lines.filter(line => line.properties?.[TOKEN] === row.token);
        if (!found.length) {
          if (row.status === 'pending' && row.rejected) { row.status = 'rejected'; changed = true; continue; }
          if (row.status === 'pending' || row.status === 'confirmed') throw failure('cart_unconfirmed', true);
          continue;
        }
        const line = found[0];
        if (found.length !== 1 || String(line.product_id) !== productId || String(line.variant_id ?? line.id) !== row.variantId || line.quantity !== row.quantity ||
            !KEY.test(line.key || '') || !line.key.startsWith(row.variantId + ':') || planId(line) !== null || line.properties?.[PAYLOAD] !== row.payloadHash) throw failure('cart_unconfirmed', true);
        const actualHash = await propertiesHash(line.properties || {});
        if (row.cartHash && row.cartHash !== actualHash) throw failure('cart_unconfirmed', true);
        if (row.status !== 'confirmed' || row.key !== line.key) changed = true;
        row.status = 'confirmed'; row.key = line.key; row.cartHash = actualHash;
      }
      if (changed) persist(journal);
    }
    async function finish(journal, lines, onProgress) {
      if (journal.rows.some(row => row.status !== 'confirmed')) return publicState(journal);
      if (journal.phase === 'complete') return { ...publicState(journal), currency, cartUrl };
      if (journal.source) {
        const source = journal.source, replacement = journal.rows[0];
        const old = lines.find(line => line.key === source.key);
        if (old) {
          // Repeated quantity-zero writes are avoided too: an earlier removal
          // may still complete, or the old line may have been re-created.
          if (journal.phase === 'removing') throw failure('cart_replace_unconfirmed', true);
          if (!await matchesSource(old, source, lines) || old.key === replacement.key) throw failure('cart_source_changed');
          journal.phase = 'removing'; persist(journal); progress(onProgress, { stage: 'replace', token: replacement.token });
          try { await request(urls.change, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: source.key, quantity: 0 }) }); }
          catch { /* Confirm the outcome; never infer failure from lost HTTP. */ }
          try { lines = await cart(); await confirmRows(journal, lines); } catch { throw failure('cart_replace_unconfirmed', true); }
          if (lines.some(line => line.key === source.key)) throw failure('cart_replace_unconfirmed', true);
        }
        if (await relatedSource(lines, source, replacement.key)) throw failure('cart_replace_unconfirmed', true);
      }
      journal.phase = 'complete'; persist(journal); progress(onProgress, { stage: 'done' });
      return { ...publicState(journal), currency, cartUrl };
    }
    async function restore(lineKey) {
      return exclusive(async () => {
        if (!KEY.test(lineKey || '')) throw failure('cart_restore_unavailable');
        const lines = await cart(), found = lines.filter(line => line.key === lineKey);
        if (found.length !== 1) throw failure('cart_restore_unavailable');
        const source = Object.freeze(await sourceFromLine(found[0], lines));
        return { line: copy(found[0]), source };
      });
    }
    // Read-only ownership check for an explicit quick-editor → Studio upgrade.
    // It never adopts, clears or recovers this editor's cart journal.
    async function inspectSource(source, { replacementKey = null } = {}) {
      return exclusive(async () => {
        const expected = cleanSource(source, productId);
        if (replacementKey !== null && !KEY.test(replacementKey)) throw failure('cart_invalid');
        const lines = await cart(), found = lines.filter(line => line.key === expected.key);
        if (found.length > 1) return { status: 'changed' };
        const related = await relatedSource(lines.filter(line => line.key !== expected.key), expected, replacementKey);
        if (related) return { status: 'related' };
        if (!found.length) return { status: 'missing' };
        if (expected.key === replacementKey || !await matchesSource(found[0], expected, lines)) return { status: 'changed' };
        return { status: 'exact', source: copy(expected), line: copy(found[0]) };
      });
    }
    async function recover({ onProgress } = {}) {
      return exclusive(async () => {
        const journal = read(); if (!journal) return null;
        progress(onProgress, { stage: 'confirm' });
        let lines; try { lines = await cart(); } catch { throw failure('cart_unconfirmed', true); }
        await confirmRows(journal, lines);
        return finish(journal, lines, onProgress);
      });
    }
    function fixedGiftConfiguration(value) {
      if (kind !== 'gift' || typeof value !== 'string' || value.length > 64000) return false;
      try {
        const configuration = JSON.parse(value), design = configuration?.design;
        return configuration?.kind === 'gift' && configuration.version === 1 && design?.version === 1 && design.designType === 'gift' &&
          ['bridal-party', 'girls-trip', 'rustic-antler-monogram'].includes(design.style);
      } catch { return false; }
    }
    async function addBatch(items, { source = null, onProgress } = {}) {
      return exclusive(async () => {
        const prepared = await prepare(items), clean = source ? cleanSource(source, productId) : null;
        const quantityChanged = clean && prepared[0]?.quantity !== clean.quantity;
        if (clean && (prepared.length !== 1 || prepared[0].variantId !== clean.variantId || quantityChanged && !fixedGiftConfiguration(prepared[0].form.get('properties[_Gift configuration]')))) throw failure('cart_source_changed');
        let journal = read(), existing = Boolean(journal);
        if (journal) {
          if (JSON.stringify(journal.source) !== JSON.stringify(clean) || journal.rows.length !== prepared.length || journal.rows.some((row, index) =>
            ['token', 'variantId', 'quantity', 'payloadHash'].some(key => row[key] !== prepared[index][key]))) throw failure('cart_batch_conflict', journal.phase !== 'complete');
        }
        let lines;
        try { lines = await cart(); } catch { throw failure(existing ? 'cart_unconfirmed' : 'cart_unavailable', existing); }
        if (!journal) {
          if (clean && quantityChanged && !fixedGiftConfiguration(lines.find(line => line.key === clean.key)?.properties?.['_Gift configuration'])) throw failure('cart_source_changed');
          if (clean && !await matchesSource(lines.find(line => line.key === clean.key), clean, lines)) throw failure('cart_source_changed');
          journal = { version: 1, id: crypto.randomUUID(), productId, root: base.pathname, phase: 'adding', source: clean,
            rows: prepared.map(({ token, variantId, quantity, payloadHash }) => ({ token, variantId, quantity, payloadHash, status: 'ready', rejected: false })) };
          persist(journal);
        }
        await confirmRows(journal, lines);
        if (journal.phase === 'removing' || journal.phase === 'complete') return finish(journal, lines, onProgress);
        for (let index = 0; index < journal.rows.length; index++) {
          const row = journal.rows[index];
          if (row.status === 'confirmed') continue;
          // Only a subsequent explicit addBatch invocation may retry a rejected
          // row. A still-pending row cannot get here after reconciliation.
          if (!['ready', 'rejected'].includes(row.status)) throw failure('cart_unconfirmed', true);
          if (journal.source && !await matchesSource(lines.find(line => line.key === journal.source.key), journal.source, lines)) throw failure('cart_source_changed');
          row.status = 'pending'; row.rejected = false; persist(journal);
          progress(onProgress, { stage: 'upload', index, total: journal.rows.length, token: row.token });
          try {
            const response = await request(urls.add, { method: 'POST', body: prepared[index].form });
            if ([400, 404, 422].includes(response.status) && response.data?.status === response.status &&
                (typeof response.data.description === 'string' || typeof response.data.message === 'string')) {
              row.rejected = true; persist(journal);
            }
          } catch { /* The server may have accepted the file and cart line. */ }
          progress(onProgress, { stage: 'confirm', index, total: journal.rows.length, token: row.token });
          try { lines = await cart(); } catch { throw failure('cart_unconfirmed', true); }
          await confirmRows(journal, lines);
          if (row.status === 'rejected') throw failure('cart_rejected');
          if (row.status !== 'confirmed') throw failure('cart_unconfirmed', true);
        }
        return finish(journal, lines, onProgress);
      });
    }
    function clearCompleted() {
      if (running.has(storageKey)) throw failure('cart_busy', true);
      const journal = read(); if (!journal) return;
      if (journal.phase !== 'complete') throw failure('cart_unconfirmed', true);
      try {
        sessionStorage.removeItem(storageKey);
        if (sessionStorage.getItem(storageKey)) throw failure('cart_storage_unavailable');
      } catch { throw failure('cart_storage_unavailable', true); }
    }
    async function abandonChecked() {
      // Explicit shopper acknowledgement only. This never removes accepted
      // bottles and cannot bypass an unknown add or an unfinished replacement.
      return exclusive(async () => {
        const journal = read(); if (!journal) return null;
        if (journal.phase === 'removing' || journal.source && journal.phase !== 'complete') throw failure('cart_replace_unconfirmed', true);
        if (journal.rows.some(row => row.status === 'pending')) throw failure('cart_unconfirmed', true);
        let lines; try { lines = await cart(); } catch { throw failure('cart_unconfirmed', true); }
        await confirmRows(journal, lines);
        const result = publicState(journal);
        try {
          sessionStorage.removeItem(storageKey);
          if (sessionStorage.getItem(storageKey)) throw failure('cart_storage_unavailable');
        } catch { throw failure('cart_storage_unavailable', true); }
        return { ...result, abandoned: true, cartUrl };
      });
    }
    return Object.freeze({ addBatch, recover, restore, inspectSource, inspect: () => publicState(read()), clearCompleted, abandonChecked, cartUrl });
  }
  root.JEBirthflowerCart = Object.freeze({ create, propertiesHash, tokenProperty: '_Birth flower submission', payloadProperty: '_Birth flower payload' });
})(typeof globalThis !== 'undefined' ? globalThis : window);
